import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AdminLoginPage from './pages/AdminLoginPage';
import DashboardPage from './pages/DashboardPage';
import NewCasePage from './pages/NewCasePage';
import AnalysisPage from './pages/AnalysisPage';
import CaseHistoryPage from './pages/CaseHistoryPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import PatientHistoryPage from './pages/PatientHistoryPage';
import HelpPage from './pages/HelpPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import { Toaster } from './components/ui/sonner';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './components/ui/dropdown-menu';
import { Activity, LayoutDashboard, Upload, FolderOpen, CreditCard, LogOut, User, Menu, UserCog, ShieldCheck, HelpCircle, Info, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { usePageTracker } from './hooks/usePageTracker';
import '@/App.css';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/new-case', icon: Upload, label: 'New Case' },
  { to: '/history', icon: FolderOpen, label: 'Case History' },
  { to: '/subscription', icon: CreditCard, label: 'Subscription' },
  { to: '/profile', icon: UserCog, label: 'Profile' },
];

const infoItems = [
  { to: '/help', icon: HelpCircle, label: 'Help' },
  { to: '/about', icon: Info, label: 'About Us' },
  { to: '/contact', icon: MessageSquare, label: 'Contact Us' },
];

function Sidebar({ mobileOpen, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const mainItems = isAdmin ? [...navItems, { to: '/admin', icon: ShieldCheck, label: 'Admin Panel' }] : navItems;

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={onClose} />}
      <aside className={`fixed top-0 left-0 h-full w-60 bg-white border-r border-stone-200 z-50 transition-transform lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:static lg:z-auto flex flex-col`}>
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-stone-100 shrink-0">
          <Activity className="w-6 h-6 text-[#1A4D2E]" />
          <div className="leading-tight">
            <span className="text-base font-heading font-semibold tracking-tight text-stone-900 block">ChestBuddy</span>
            <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-stone-400">GP-Assist</span>
          </div>
        </div>
        <nav className="p-3 space-y-1 flex-1 overflow-auto" data-testid="nav-sidebar">
          {mainItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              onClick={onClose}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? 'bg-[#1A4D2E]/10 text-[#1A4D2E]' : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px]" />
              {item.label}
            </NavLink>
          ))}

          <div className="pt-4 pb-1 px-3">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-stone-400">Information</p>
          </div>
          {infoItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? 'bg-[#1A4D2E]/10 text-[#1A4D2E]' : 'text-stone-500 hover:bg-stone-50 hover:text-stone-700'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}

function TopBar({ onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'DR';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="h-16 border-b border-stone-200 bg-white flex items-center justify-between px-4 lg:px-6">
      <button onClick={onMenuClick} className="lg:hidden p-2 rounded-lg hover:bg-stone-50" data-testid="mobile-menu-btn">
        <Menu className="w-5 h-5 text-stone-600" />
      </button>
      <div className="lg:hidden flex items-center gap-2">
        <Activity className="w-5 h-5 text-[#1A4D2E]" />
        <span className="font-heading font-semibold text-stone-900">ChestBuddy</span>
      </div>
      <div className="hidden lg:block" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 p-1 rounded-lg hover:bg-stone-50 transition-colors" data-testid="user-menu-btn">
            <Avatar className="w-8 h-8 bg-[#1A4D2E]">
              <AvatarFallback className="bg-[#1A4D2E] text-white text-xs font-medium">{initials}</AvatarFallback>
            </Avatar>
            <span className="hidden sm:block text-sm font-medium text-stone-700">{user?.name}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem className="text-sm gap-2 text-stone-600">
            <User className="w-4 h-4" /> {user?.email}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="nav-profile" onClick={() => navigate('/profile')} className="text-sm gap-2 text-stone-600 cursor-pointer">
            <UserCog className="w-4 h-4" /> My Profile
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/help')} className="text-sm gap-2 text-stone-600 cursor-pointer">
            <HelpCircle className="w-4 h-4" /> Help
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="nav-logout" onClick={handleLogout} className="text-sm gap-2 text-red-600 focus:text-red-700">
            <LogOut className="w-4 h-4" /> Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onMenuClick={() => setMobileOpen(true)} />
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">{children}</main>
      </div>
      <FloatingWhatsApp />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A4D2E] border-t-transparent rounded-full" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function OptionalAuthRoute({ children }) {
  // Used for /subscription — accessible to anon users (checkout-first signup),
  // but wraps in Layout for logged-in users.
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Layout>{children}</Layout>;
  return (
    <div className="min-h-screen bg-stone-50">
      <main className="p-4 md:p-6 lg:p-8">{children}</main>
    </div>
  );
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    // Preserve coupon param from WhatsApp links → land on subscription page directly
    const couponParam = new URLSearchParams(window.location.search).get('coupon');
    if (couponParam) return <Navigate to={`/subscription?coupon=${encodeURIComponent(couponParam)}`} replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function PageTracker() {
  usePageTracker();
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageTracker />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/new-case" element={<ProtectedRoute><NewCasePage /></ProtectedRoute>} />
          <Route path="/case/:id" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><CaseHistoryPage /></ProtectedRoute>} />
          <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/patient/:patientId" element={<ProtectedRoute><PatientHistoryPage /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
          <Route path="/about" element={<ProtectedRoute><AboutPage /></ProtectedRoute>} />
          <Route path="/contact" element={<ProtectedRoute><ContactPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
